import React from 'react';

import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from './utils';


/**
 * Tabs, in two looks. Built on @radix-ui/react-tabs for roving focus and a11y.
 *
 * `underline` (default) — a hairline-bottom track with a 2px active underline in
 * primary, active label ink + semibold, idle ink-muted + medium. Matches the
 * .pen desktop detail panels.
 *
 * `segmented` — the boxed control from DESIGN.md's `segmented` token and the
 * .pen phone frames ("M Part Detail" Tabs `Ob05N`, "M Listing Copy"): a
 * `surface-2` hairline box with 3px padding whose active segment lifts to
 * `surface-1`. Underline tabs are near-invisible on a 390px screen; this is the
 * phone form of the SAME tab set, so pass the variant by breakpoint rather than
 * rendering two lists (two lists in one Root would duplicate Radix's generated
 * `id`/`aria-controls` pairs).
 *
 * `components/filters/SegmentTabs.jsx` is deliberately NOT touched: it is a
 * value/onChange filter control with 9 filter-bar consumers, not a tab panel
 * switcher. This gives the Tabs primitive the same look for the tab case
 * instead of adding a third segmented implementation.
 *
 *   <Tabs defaultValue="details">
 *     <TabsList variant="segmented">
 *       <TabsTrigger variant="segmented" value="details">Details</TabsTrigger>
 *       <TabsTrigger variant="segmented" value="fitment">Fitment</TabsTrigger>
 *     </TabsList>
 *     <TabsContent value="details">…</TabsContent>
 *   </Tabs>
 */
const Tabs = TabsPrimitive.Root;

const LIST_VARIANTS = {
  underline: 'gap-5 border-b border-hairline',
  segmented: 'gap-0 rounded-lg border border-hairline bg-surface-2 p-[3px]',
};

// NOTE: layout classes (`flex`, `items-center`, `justify-*`) belong to the
// SEGMENTED variant only, never to the shared base. A Radix Trigger renders a
// `<button>`, which is `inline-block`; hoisting `flex items-center` into the
// base silently changed every existing underline consumer — it activated the
// previously-inert `gap-1.5` on `Intake/VisualPicker.jsx` (re-spacing its
// zone-count badge) and re-aligned the badge from baseline to centre, and it
// changed the layout context of `Inventory/PublishModal.jsx`'s `<TabFace>`
// child. The underline string below is byte-identical to the pre-variant
// component; keep it that way.
const TRIGGER_VARIANTS = {
  underline: 'border-b-2 border-transparent px-[3px] py-3 text-ink-muted hover:text-ink '
    + 'focus-visible:rounded-sm '
    + 'data-[state=active]:border-primary data-[state=active]:text-ink',
  // The .pen box is 42px tall, but a tab is a tap target and DESIGN.md sets the
  // floor at 44px — the segment, not the frame, wins here.
  segmented: 'flex min-h-11 flex-1 items-center justify-center rounded-md px-2 '
    + 'text-ink-subtle hover:text-ink '
    + 'data-[state=active]:bg-surface-1 data-[state=active]:text-ink',
};

const TabsList = React.forwardRef((props, ref) => {
  const { className, variant = 'underline', ...restProps } = props;
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn('flex items-center', LIST_VARIANTS[variant], className)}
      {...restProps}
    />
  );
});
TabsList.displayName = 'TabsList';

const TabsTrigger = React.forwardRef((props, ref) => {
  const { className, variant = 'underline', ...restProps } = props;
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'cursor-pointer text-table font-medium',
        'outline-none transition-colors focus-visible:ring-focus',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[state=active]:font-semibold',
        TRIGGER_VARIANTS[variant],
        className,
      )}
      {...restProps}
    />
  );
});
TabsTrigger.displayName = 'TabsTrigger';

const TabsContent = React.forwardRef((props, ref) => {
  const { className, ...restProps } = props;
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn('outline-none focus-visible:ring-focus', className)}
      {...restProps}
    />
  );
});
TabsContent.displayName = 'TabsContent';

export {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
};
export default Tabs;
